import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';

import { SYSTEM_MAINTAINANCE_TAB_OPTIONS } from 'src/app/lib/constants/tabs.constants';
import { 
  BUYLOAD_REBATE_TABLE_SUB_HEADERS,
  DISTRIBUTOR_GROUP_TABLE_SUB_HEADERS
} from 'src/app/lib/constants/transactions.constants';
import { PopUpDialogService } from 'src/app/services/popup-dialog.service';
import { MegapayLoaderComponent } from 'src/app/shared/component/megapay-loader/megapay-loader.component';
import { SharedComponentsModule } from 'src/app/shared/modules/shared-component.module';
import { SharedDirectivesModule } from 'src/app/shared/modules/shared-directives.module';
import { SharedPipeModule } from 'src/app/shared/modules/shared-pipe.module';
import { DropdownFieldV2Component } from 'src/app/shared/component/custom-dropdown-v2/custom-dropdown-v2.component';

import { RebateService } from './rebate.service';
import { CreateRebateComponent } from './create-rebate/create-rebate.component';
import { EditRebateComponent } from './edit-rebate/edit-rebate.component';
import { AuthService } from 'src/app/core/services/auth.service';
import { EditDistributorRebateComponent, EditDistributorRebateDialogData } from './edit-distributor-rebate/edit-distributor-rebate.component';
import { GetDistributorsRebatesResponse } from 'src/app/core/model/server/vo/GetDistributorsRebatesResponse.inteface';
import { EditDistributorRebateRequest } from 'src/app/core/model/server/vo/EditDistributorRebateRequest.interface';
@Component({
  selector: 'app-rebate-settings',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    SharedComponentsModule,
    SharedDirectivesModule,
    SharedPipeModule,
    NgxSpinnerModule,
    MegapayLoaderComponent,
    DropdownFieldV2Component,
    MatTooltipModule,
  ],
  templateUrl: './rebate-settings.component.html',
  styleUrl: './rebate-settings.component.scss',
})
export class RebateSettingsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  tabs           = SYSTEM_MAINTAINANCE_TAB_OPTIONS;
  buyloadtableSubHeader = BUYLOAD_REBATE_TABLE_SUB_HEADERS;
  distributorTableSubHeader = DISTRIBUTOR_GROUP_TABLE_SUB_HEADERS;
  

  isLoading      = false;
  isLoadingTiers = false;

  // ─── Tier dropdown ────────────────────────────────────────────────────────
  tierOptions: { label: string; value: number }[] = [];
  selectedTierId: number | null = null;

  get selectedTierLabel(): string {
    return this.tierOptions.find(o => o.value === this.selectedTierId)?.label ?? '';
  }

  // ─── Table data ───────────────────────────────────────────────────────────
  itemLists: any[] = [];

  // ─── Distributor ───────────────────────────────────────────────────────────
  distributorRebates: GetDistributorsRebatesResponse | null = null;
  isLoadingDistributor = false;
  distributorRows: { type: 'rd' | 'pd' | 'rt'; label: string; value: number | null }[] = [];


  // ─── Disable guard ────────────────────────────────────────────────────────
  disablingUid: number | null = null;

  constructor(
    public rebateService: RebateService,    
    private toastr: ToastrService,
    private spinner: NgxSpinnerService,
    private popUpDialog: PopUpDialogService,
    private dialog: MatDialog,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.loadTiers();
    this.loadDistributorRebates();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.spinner.hide().catch(console.error);
  }

  // ─── Load tiers ───────────────────────────────────────────────────────────

  loadTiers(): void {
    this.isLoadingTiers = true;

    this.rebateService.getTierOptions()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (options) => {
          this.tierOptions = options;
        },
        error: (err) => {
          this.toastr.error('Failed to load tiers. Please try again.');
        },
        complete: () => {
          this.isLoadingTiers = false;
          if (this.tierOptions.length > 0) {
            this.selectedTierId = this.tierOptions[0].value;
            this.loadRebates(this.selectedTierId);
          }
        },
      });
  }

  // ─── Load rebates ─────────────────────────────────────────────────────────

  loadRebates(tierId: number): void {
    this.isLoading = true;
    this.spinner.show().catch(console.error);

    this.rebateService.getRebatesByTier(tierId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (rows) => {
          this.itemLists = rows;
        },
        error: (err) => {
          this.toastr.error('Failed to load buyload rebates. Please try again.');
        },
        complete: () => {
          this.isLoading = false;
          this.spinner.hide().catch(console.error);
        },
      });
  }

  loadDistributorRebates(): void {
    this.isLoadingDistributor = true;

    this.rebateService.getDistributorsRebates()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.distributorRebates = res;
          this.distributorRows = [
            { type: 'rd', label: 'Regional Distributor (RD)', value: res.rdRebatePercentage },
            { type: 'pd', label: 'Provincial Distributor (PD)', value: res.pdRebatePercentage },
            { type: 'rt', label: 'Retailer (RT)',               value: res.rtRebatePercentage },
          ];
        },
        error: (err) => {
          this.toastr.error('Failed to load distributor rebates.');
        },
        complete: () => {
          this.isLoadingDistributor = false;
        },
      });
  }

  // ─── Tier change ──────────────────────────────────────────────────────────

  onTierChange(option: { label: string; value: any }): void {
    this.selectedTierId = option.value;
    this.loadRebates(this.selectedTierId!);
  }

  // ─── Open Create dialog ───────────────────────────────────────────────────

  openCreateDialog(): void {
    const dialogRef = this.dialog.open(CreateRebateComponent, {
      width: '380px',
      data: { tierId: this.selectedTierId, tierLabel: this.selectedTierLabel },
      disableClose: true,
    });

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((created: boolean) => {
        if (created && this.selectedTierId !== null) {
          this.loadRebates(this.selectedTierId);
        }
      });
  }

  openEditDistributorDialog(row: { type: 'rd' | 'pd' | 'rt'; label: string; value: number | null }): void {
    if (!this.distributorRebates) { return; }

    (document.activeElement as HTMLElement)?.blur();

    const dialogRef = this.dialog.open(EditDistributorRebateComponent, {
      width: '380px',
      disableClose: true,
      data: {
        type:           row.type,
        label:          row.label,
        currentValue:   row.value,
        currentRebates: this.distributorRebates,
      } as EditDistributorRebateDialogData,
    });

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((updated: boolean) => {
        if (updated) { this.loadDistributorRebates(); }
    });
  }

  resetDistributorRebatesConfirmation(row: { type: 'rd' | 'pd' | 'rt'; label: string; value: number | null }): void {
    this.popUpDialog.openInfoActionDialog(
      `Are you sure you want to reset<br><strong>${row.label}</strong> rebate to 0%?`,
      () => this.executeResetDistributorRebate(row),
    );
  }


  // ─── Open Edit dialog ─────────────────────────────────────────────────────

  openEditDialog(item: any): void {
    const dialogRef = this.dialog.open(EditRebateComponent, {
      width: '380px',
      data: { item, tierId: this.selectedTierId, tierLabel: this.selectedTierLabel },
      disableClose: true,
    });

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((updated: boolean) => {
        if (updated && this.selectedTierId !== null) {
          this.loadRebates(this.selectedTierId);
        }
      });
  }

  // ─── Disable (Delete) ─────────────────────────────────────────────────────

  toggleStatusConfirmation(item: any): void {
    const enabling = !item.isEnabled;
    const action   = enabling ? 'enable' : 'disable';

    this.popUpDialog.openInfoActionDialog(
      `
        Are you sure you want to ${action}<br> this rebate?
      `,
      () => this.executeToggle(item, enabling),
    );
  }
  

  getRefundButtonState(): { disabled: boolean; tooltip: string } {
    if (!this.authService.isAdmin()) {
      return { disabled: true, tooltip: 'Only admins can create, edit, or delete rebates. Please contact an admin if you need assistance.' };
    }
    return { disabled: false, tooltip: '' };
  }

  private executeToggle(item: any, enable: boolean): void {
    this.disablingUid = item.uid;
    this.spinner.show().catch(console.error);

    this.rebateService.toggleRebateStatus(item, this.selectedTierId!, enable)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          if (response.status === 200) {
            if (this.selectedTierId !== null) {
              this.loadRebates(this.selectedTierId);
            }
            this.toastr.success(
              enable
                ? 'Rebate Enabled.'
                : 'Rebate Disabled.'
            );
          } else {
            this.popUpDialog.openErrorDialog(
              `Failed to ${enable ? 'enable' : 'disable'} rebate. Please try again or contact the developer.`,
            );
          }
        },
        error: (err) => {
          this.popUpDialog.openErrorDialog(
            `An error occurred. Please refresh or contact the developer.`,
          );
        },
        complete: () => {
          this.disablingUid = null;
          this.spinner.hide().catch(console.error);
        },
      });
  }

  private executeResetDistributorRebate(row: { type: 'rd' | 'pd' | 'rt'; label: string; value: number | null }): void {
    if (!this.distributorRebates) { return; }

    const existing = this.distributorRebates;
   const payload: EditDistributorRebateRequest = {
    rdRebatePercentage: row.type === 'rd' ? null : existing.rdRebatePercentage,
    pdRebatePercentage: row.type === 'pd' ? null : existing.pdRebatePercentage,
    rtRebatePercentage: row.type === 'rt' ? null : existing.rtRebatePercentage,
  };

    this.spinner.show().catch(console.error);

    this.rebateService.modifyDistributorRebates(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          if (response.status === 200) {
            this.toastr.success(`${row.label} rebate has been reset to 0%.`);
            this.loadDistributorRebates();
          } else {
            this.popUpDialog.openErrorDialog('Failed to reset rebate. Please try again.');
          }
        },
        error: (err) => {
          this.popUpDialog.openErrorDialog('An error occurred. Please refresh or contact the developer.');
        },
        complete: () => {
          this.spinner.hide().catch(console.error);
        },
      });
  }
}
